import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';

export default function TutorDirectory() {
  const navigate = useNavigate();
  const [tutors, setTutors] = React.useState([]);
  const [subject, setSubject] = React.useState("All Subjects");
  const [school, setSchool] = React.useState("Mira Costa");
  const [filtered, setFiltered] = React.useState([]);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${API_BASE_URL}/api/tutors`)
      .then(res => res.json())
      .then(data => {
        console.log('Fetched tutors:', data); // Debug log
        setTutors(data);
        // Default: show Mira Costa tutors (Costa, Mira Costa, or no school set)
        const defaultFiltered = data.filter(t => isMiraCostaSchool(t.school));
        setFiltered(defaultFiltered);
      })
      .catch(err => {
        console.error('Error fetching tutors:', err);
      });
  }, []);

  // Mira Costa: DB value 'Costa' or 'Mira Costa', or no school (null/empty) = Manhattan Beach / Mira Costa tutors
  const isMiraCostaSchool = (s) => {
    const v = (s || '').trim();
    return v === '' || v === 'Costa' || v === 'Mira Costa';
  };

  function handleFilter() {
    setFiltered(
      tutors.filter(tutor => {
        const schoolMatch = school === "All Schools" ||
          (school === "Mira Costa" && isMiraCostaSchool(tutor.school)) ||
          ((tutor.school || '').trim() === school);
        const subjectMatch = subject === "All Subjects" || 
          (tutor.subject && tutor.subject.toLowerCase() === subject.toLowerCase()) ||
          (Array.isArray(tutor.subjects) && tutor.subjects.some(s => s.toLowerCase() === subject.toLowerCase()));
        return schoolMatch && subjectMatch;
      })
    );
  }

  // Get all unique subjects for the dropdown (from current tutors, subject to school filter)
  const getAllSubjects = () => {
    const subjects = new Set();
    tutors.forEach(tutor => {
      const schoolMatch = school === "All Schools" ||
        (school === "Mira Costa" && isMiraCostaSchool(tutor.school)) ||
        ((tutor.school || '').trim() === school);
      if (!schoolMatch) return;
      if (tutor.subject) subjects.add(tutor.subject);
      if (Array.isArray(tutor.subjects)) {
        tutor.subjects.forEach(s => subjects.add(s));
      }
    });
    return Array.from(subjects).sort();
  };

  // Get school options for dropdown: unique display names (Costa, Mira Costa, or no school -> "Mira Costa")
  const getSchoolOptions = () => {
    const displayNames = new Set();
    tutors.forEach(tutor => {
      const s = (tutor.school || '').trim();
      if (isMiraCostaSchool(tutor.school)) displayNames.add('Mira Costa');
      else if (s) displayNames.add(s);
    });
    const rest = Array.from(displayNames).filter(d => d !== 'Mira Costa').sort();
    const includeMiraCosta = displayNames.has('Mira Costa') || tutors.length === 0;
    return ['All Schools', ...(includeMiraCosta ? ['Mira Costa'] : []), ...rest];
  };

  return (
    <div>
      <Header />
      <div style={{padding: '2.5rem', background: '#f7f9fb', minHeight: '100vh'}}>
        <h1 style={{fontSize: '2.3rem', marginBottom: 28, fontWeight: 800, letterSpacing: 1}}>Manhattan Beach Tutor Directory</h1>
	<p style={{marginBottom: 28, fontSize: "1.05rem", color: "#333"}}>
          This directory is only to be used to book tutors for Manhattan Beach School District students. All of these tutors are members of the top Honor Societies at Mira Costa High School. To protect their privacy, we do not list individual names here. If you'd like us to match you with a tutor instead of choosing one below, please email us at honorsocietyscholars@gmail.com.
        </p>
        <p style={{marginBottom: 28, fontSize: "1.05rem", color: "#333"}}>
          If you are looking for tutoring for a Lawndale student, please visit our <Link to="/directory/ja" style={{color: '#1976d2', textDecoration: 'underline'}}>Lawndale Tutor Directory here.</Link>
        </p>
        <p style={{marginBottom: 28, fontSize: "1.05rem", color: "#333"}}>
          MBMS 8th Graders: We also offer <b>free mentorship sessions</b> with Mira Costa Honor Society students to help you prepare for your freshman year of high school.{' '}
          <Link to="/directory/MBMentor" style={{color: '#04d664', textDecoration: 'underline'}}>Click here to view our Mentor Directory.</Link>
        </p>
        <div style={{
            marginBottom: 32,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '1rem' : 12,
            alignItems: 'stretch'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: 'center',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 12px #e0e0e0',
            padding: isMobile ? '1rem' : '1rem 1.5rem',
            maxWidth: isMobile ? '100%' : 560
          }}>
            <select
              value={school}
              onChange={e => setSchool(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #bbb',
                borderRadius: 8,
                fontSize: 16,
                flex: 1,
                minWidth: 0
              }}
            >
              {getSchoolOptions().map(opt => (
                <option key={opt}>{opt}</option>
              ))}
            </select>
            <select
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #bbb',
                borderRadius: 8,
                fontSize: 16,
                flex: 1,
                minWidth: 0
              }}
            >
              <option>All Subjects</option>
              {getAllSubjects().map(subj => (
                <option key={subj}>{subj}</option>
              ))}
            </select>
            <button
              onClick={handleFilter}
              style={{
                background: '#04d664',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '0.6rem 1.5rem',
                fontWeight: 600,
                fontSize: 16,
                cursor: 'pointer',
                boxShadow: '0 1px 4px #cceedd'
              }}
            >Filter</button>
          </div>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 12px #e0e0e0',
            padding: isMobile ? '1rem' : '1rem 1.5rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Link
              to="/directory/ja"
              style={{
                background: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '0.6rem 1.5rem',
                fontWeight: 600,
                fontSize: 16,
                cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(25, 118, 210, 0.4)',
                textDecoration: 'none',
                whiteSpace: 'nowrap'
              }}
            >Lawndale Schools</Link>
          </div>
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 32,
          justifyContent: 'flex-start',
          marginTop: 8
        }}>
          {filtered.map(tutor => {
            // Combine subject and subjects for display
            const displaySubjects = [];
            if (tutor.subject) displaySubjects.push(tutor.subject);
            if (Array.isArray(tutor.subjects)) {
              tutor.subjects.forEach(s => {
                if (!displaySubjects.includes(s)) displaySubjects.push(s);
              });
            }
            
            return (
              <div key={tutor.id} style={{
                background: '#fff',
                border: 'none',
                boxShadow: '0 2px 16px #e0e0e0',
                padding: '1.5rem',
                width: 260,
                borderRadius: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                fontSize: 17,
		minHeight: 320
              }}>
                <div style={{fontWeight: 700, fontSize: 18, marginBottom: 2}}>ID: {tutor.id}</div>
                <div><b>Subjects:</b> {displaySubjects.length > 0 ? displaySubjects.join(', ') : '—'}</div>
                <div><b>Available Time:</b> {tutor.time || '—'}</div>
                <div><b>Gender:</b> {tutor.gender || '—'}</div>
                <div><b>Graduation Year:</b> {tutor.gradYear || '—'}</div>
                <div style={{marginTop: 'auto', display: 'flex', gap: 10, justifyContent: 'center'}}>
                  <button onClick={() => navigate(`/register?tutor=${tutor.id}`)} style={{
                    background: '#04d664',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '0.5rem 1.2rem',
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px #cceedd'
                  }}>Book</button>
                  {/* <button onClick={() => navigate(`/tutor/${tutor.id}`)} style={{
                    background: '#eee',
                    color: '#222',
                    border: 'none',
                    borderRadius: 8,
                    padding: '0.5rem 1.2rem',
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px #eee'
                  }}>More</button> */}
                </div>
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#666',
            fontSize: '1.1rem'
          }}>
            No tutors found matching your criteria.
          </div>
        )}
      </div>
    </div>
  );
}
